import type { ProjectPdfItem } from '@/services/projectPdfs'
import type { ProjectStateItem, ProjectStateSource } from '@/services/projectStates'

/** One row in the "saved project states" list on the summary page. */
export interface ProjectStateEntry {
  key: string
  /** null for PDFs that predate project states (download only). */
  stateId: string | null
  timestamp: number | null
  createdAt: string
  label: string | null
  source: ProjectStateSource | 'legacy'
  pdf: ProjectPdfItem | null
}

/**
 * Merges saved states with stored PDFs: states carry their PDF (if any), PDFs
 * without a state stay listed as download-only entries. Automatic backups
 * (created before a reset) are listed; states created for project exports are
 * not, they only exist for the export/import flow.
 * Newest first.
 */
export function buildProjectStateEntries(states: ProjectStateItem[], pdfs: ProjectPdfItem[]): ProjectStateEntry[] {
  const pdfById = new Map(pdfs.map((pdf) => [pdf.id, pdf]))
  const referencedPdfIds = new Set<string>()

  const listedStates = states.filter((state) => state.source !== 'export')
  const stateEntries: ProjectStateEntry[] = listedStates.map((state) => {
    const pdf = state.pdfId ? (pdfById.get(state.pdfId) ?? null) : null
    if (state.pdfId) referencedPdfIds.add(state.pdfId)
    return {
      key: `state-${state.id}`,
      stateId: state.id,
      timestamp: state.timestamp,
      createdAt: state.createdAt,
      label: state.label,
      source: state.source,
      pdf,
    }
  })

  const legacyEntries: ProjectStateEntry[] = pdfs
    .filter((pdf) => !referencedPdfIds.has(pdf.id))
    .map((pdf) => ({
      key: `pdf-${pdf.id}`,
      stateId: null,
      timestamp: null,
      createdAt: pdf.createdAt,
      label: pdf.name,
      source: 'legacy',
      pdf,
    }))

  return [...stateEntries, ...legacyEntries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}
